import { useState, useEffect } from 'react';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { useNotification } from 'src/contexts/NotificationContext';
import { Option, SelectBox } from 'src/components/SelectBox';
import { CopyIcon } from 'src/components/Icons';
import { Tab } from 'src/components/Tab';
import { Modal } from 'src/components/Modal';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';

interface ApiKeyResponse {
  id: string;
  apiKey: string;
  active: boolean;
  createdAt: string;
  environment: {
    id: string;
  };
}

type SavedApiKey = {
  id: string;
  createdAt: string;
  envId: string;
  apiKey: string;
};

interface WidgetConfigProps {
  /**
   * Pre-selected agent. If provided, the agent selection dropdown will be disabled
   * and this agent will be used. Otherwise, a dropdown to select from available agents will be shown.
   */
  agent?: Agent;
}

export const WidgetConfig = ({ agent }: WidgetConfigProps) => {
  const { get, remove } = useAuthedRequest();
  const notification = useNotification();
  const [token, setToken] = useState<string>('');
  const [newApiKey, setNewApiKey] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Option | null>(null);
  const [savedApiKeys, setSavedApiKeys] = useState<SavedApiKey[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);

  const expiryOptions = [
    { value: '1800', label: '30 minutes' },
    { value: '3600', label: '1 hour' },
    { value: '10800', label: '3 hours' },
    { value: '21600', label: '6 hours' },
    { value: 'never', label: 'Never expire' },
  ];
  const [expiryOption, setExpiryOption] = useState<Option>(expiryOptions[1]);

  useEffect(() => {
    if (!agent) {
      const fetchAgents = async () => {
        try {
          setLoading(true);
          const response = await get('/agents');
          const agentList = response.items || [];
          setAgents(agentList);
          if (agentList.length > 0) {
            const defaultAgent = {
              value: agentList[0].id || '',
              label: agentList[0].name || `Agent ${agentList[0].id}`,
            };
            setSelectedAgent(defaultAgent);
          }
        } catch (error) {
          notification.error('Failed to load agents');
          console.error('Error loading agents:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchAgents();
    } else if (agent.id) {
      // If agent is provided, set it as the selected agent
      setSelectedAgent({
        value: agent.id || '',
        label: agent.name || `Agent ${agent.id}`,
      });
    }

    fetchApiKeys();
  }, [notification, agent]);

  const fetchApiKeys = async () => {
    try {
      const response = (await get('/api-key')) as ApiKeyResponse[];
      const keys = response
        .filter((key: ApiKeyResponse) => key.active)
        .map((key: ApiKeyResponse) => ({
          id: key.id,
          createdAt: key.createdAt,
          envId: key.environment.id,
          apiKey: key.apiKey,
        }));
      setSavedApiKeys(keys);
    } catch (e) {
      console.error('Failed to load API keys', e);
      notification.error('Failed to load API keys');
    }
  };

  const handleDeleteClick = (keyId: string) => {
    setKeyToDelete(keyId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (keyToDelete) {
      try {
        await remove(`/api-key/${keyToDelete}`);
        await fetchApiKeys();
        notification.success('API Signin Key removed successfully');
      } catch (error) {
        console.error('Failed to remove API key:', error);
        notification.error('Failed to remove API key');
      }
      setKeyToDelete(null);
    }
    setDeleteConfirmOpen(false);
  };

  const cancelDelete = () => {
    setKeyToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const agentOptions = agents.map((a) => ({
    value: a.id || '',
    label: a.name || `Agent ${a.id}`,
  }));

  const generateToken = async () => {
    setLoading(true);
    try {
      let url = '/web-widget/token';
      const params = new URLSearchParams();

      if (expiryOption.value !== 'never') {
        params.append('expiresIn', expiryOption.value);
      }

      // Use the pre-selected agent if provided, otherwise use the selected agent from dropdown
      const agentId = agent?.id || (selectedAgent?.value as string);
      params.append('agentId', agentId || '');

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await get(url);
      setToken(response.token);
      notification.success('Widget token generated successfully');
    } catch (error) {
      notification.error('Failed to generate widget token');
      console.error('Error generating widget token:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateApiKey = async () => {
    setLoading(true);
    try {
      const response = await get('/web-widget/signing-key');
      setNewApiKey(response.apiKey);
      await fetchApiKeys();
      notification.success('API Signin Key generated successfully');
    } catch (error) {
      notification.error('Failed to generate API Signin Key');
      console.error('Error generating API Signin Key:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    notification.success('Copied to clipboard');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const renderAgentSelection = () => {
    if (agent) {
      return (
        <div>
          <Label htmlFor="agent">Current Agent</Label>
          <Input
            type="text"
            value={agent.name || `Agent ${agent.id}`}
            disabled={true}
          />
          <p className="text-sm text-neutral-400 mt-1">
            This widget will be configured for the current agent
          </p>
        </div>
      );
    }

    return (
      <div>
        <Label htmlFor="agent">Agent</Label>
        <SelectBox
          options={agentOptions}
          value={selectedAgent}
          onChange={(value) => {
            const option = agentOptions.find((opt) => opt.value === value);
            if (option) {
              setSelectedAgent(option);
            }
          }}
          disabled={loading || agentOptions.length === 0}
        />
        <p className="text-sm text-neutral-400 mt-1">
          Select the agent to be used with this widget (required)
        </p>
      </div>
    );
  };

  return (
    <>
      <Tab
        items={[
          {
            label: 'Direct JWT Token',
            content: (
              <div className="p-4">
                <p className="text-sm text-neutral-400 mb-4">
                  Generate a JWT token for direct use in your widget. Suitable
                  for development or simple integrations.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {renderAgentSelection()}

                  <div>
                    <Label htmlFor="expiry">Token Expiry</Label>
                    <SelectBox
                      options={expiryOptions}
                      value={expiryOption}
                      onChange={(value) => {
                        const option = expiryOptions.find(
                          (opt) => opt.value === value,
                        );
                        if (option) {
                          setExpiryOption(option);
                        }
                      }}
                      disabled={loading}
                    />
                    <p className="text-sm text-neutral-400 mt-1">
                      Select how long the widget token should be valid
                    </p>
                    {expiryOption.value === 'never' && (
                      <p className="text-sm text-warning mt-1">
                        Note: Permanent tokens should be used with caution
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-2 mb-4">
                  <Button
                    onClick={generateToken}
                    disabled={loading || (!agent && !selectedAgent)}
                  >
                    Generate Widget Token
                  </Button>
                </div>

                {token && (
                  <div className="mt-4 mb-4">
                    <Label>Your Widget Token</Label>
                    <div className="mt-1 relative">
                      <Input
                        type="text"
                        value={token}
                        readOnly
                        className="pr-10"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 px-3 flex items-center"
                        onClick={() => copyToClipboard(token)}
                      >
                        <CopyIcon className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
                      </button>
                    </div>
                    <p className="text-sm text-neutral-400 mt-1">
                      Use this token with the widget script tag
                    </p>
                  </div>
                )}

                <div className="bg-neutral-50 p-4 rounded-lg mt-6">
                  <Label className="mb-2">Widget Installation</Label>
                  <div className="text-sm mb-2">
                    Place this HTML code at the end of your body tag:
                  </div>
                  <div className="relative bg-neutral-700 text-neutral-0 p-3 rounded-md overflow-x-auto">
                    <pre className="text-xs">
                      {`<div id="webcall-container"></div>
<script src="${process.env.REACT_APP_WIDGET_URL}/fluents-web-widget.iife.js"></script>
<script>
// Initialize the widget
window.FluentsWebWidget.init({
  targetId: 'webcall-container',
  jwtToken: '${token}'
});
</script>`}
                    </pre>
                    <button
                      type="button"
                      className="absolute top-2 right-2 p-1"
                      onClick={() =>
                        copyToClipboard(
                          `<div id="webcall-container"></div>
<script src="${process.env.REACT_APP_WIDGET_URL}/fluents-web-widget.iife.js"></script>
<script>
// Initialize the widget
window.FluentsWebWidget.init({
  targetId: 'webcall-container',
  jwtToken: '${token}'
});
</script>`,
                        )
                      }
                    >
                      <CopyIcon className="h-4 w-4 text-neutral-300 hover:text-neutral-0" />
                    </button>
                  </div>
                </div>
              </div>
            ),
          },
          {
            label: 'API Signing Key',
            content: (
              <div className="p-4">
                <p className="text-sm text-neutral-400 mb-4">
                  For server-side applications, generate an API signing key to
                  create secure tokens programmatically.
                </p>

                <div className="flex items-start gap-2 mb-8">
                  <Button onClick={generateApiKey} disabled={loading}>
                    Generate API Signing Key
                  </Button>
                </div>

                {newApiKey && (
                  <div className="mt-4 mb-6 p-4 border border-warning bg-warning-100/80 rounded-lg">
                    <div className="font-semibold text-warning mb-2">
                      Your New API Signing Key
                    </div>
                    <div className="relative mb-2">
                      <Input
                        type="text"
                        value={newApiKey}
                        readOnly
                        className="pr-10 font-mono text-sm"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 px-3 flex items-center"
                        onClick={() => copyToClipboard(newApiKey)}
                      >
                        <CopyIcon className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
                      </button>
                    </div>
                    <p className="text-sm text-warning">
                      Important: Save this key somewhere safe. For security
                      reasons, we cannot show it again.
                    </p>
                  </div>
                )}

                {savedApiKeys.length > 0 && (
                  <div className="mt-4">
                    <Label className="mb-2">Your API Signing Keys</Label>
                    <div className="border rounded-md overflow-hidden">
                      <table className="min-w-full divide-y divide-neutral-100">
                        <thead className="bg-neutral-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider"
                            >
                              Created
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider"
                            >
                              Key ID
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-right text-xs font-medium text-neutral-400 uppercase tracking-wider"
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-neutral-0 divide-y divide-neutral-100">
                          {savedApiKeys.map((key) => (
                            <tr key={key.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-400">
                                {formatDate(key.createdAt)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                                {key.id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  className="text-error hover:text-error-500"
                                  onClick={() => handleDeleteClick(key.id)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="bg-neutral-50 p-4 rounded-lg mt-6">
                  <Label className="mb-2">How to use API Signing Keys</Label>
                  <div className="text-sm leading-6">
                    <p className="mb-2">
                      Use this key on your backend to generate secure tokens for
                      widget authentication:
                    </p>
                    <ol className="list-decimal pl-5 space-y-2">
                      <li>Store this signing key securely on your server</li>
                      <li>
                        When a user needs to access the widget, create a JWT
                        with:
                        <ul className="list-disc pl-5 mt-1">
                          <li>
                            <code className="bg-neutral-100 px-1 rounded">
                              agentId
                            </code>
                            : The ID of the agent to use
                            {(agent || selectedAgent) && (
                              <>
                                {' '}
                                (current agent:{' '}
                                <code className="bg-neutral-100 px-1 rounded">
                                  {agent?.id || selectedAgent?.value}
                                </code>
                                )
                              </>
                            )}
                          </li>
                          <li>
                            <code className="bg-neutral-100 px-1 rounded">
                              exp
                            </code>
                            : Expiration timestamp
                          </li>
                        </ul>
                      </li>
                      <li>
                        Sign the JWT with the signing key using HS256 algorithm
                      </li>
                      <li>
                        Provide the token to your frontend for use with the
                        widget
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            ),
          },
        ]}
      />

      <Modal
        title="Remove API Signing Key"
        isOpen={deleteConfirmOpen}
        onClose={cancelDelete}
        actionButton={
          <Button color="error" onClick={confirmDelete}>
            Remove
          </Button>
        }
      >
        <p>
          Are you sure you want to remove this API signing key? This action
          cannot be undone.
        </p>
        <p className="mt-2 text-sm text-neutral-400">
          Any applications using this key will no longer be able to generate
          widget tokens.
        </p>
      </Modal>
    </>
  );
};
